import * as React from 'react';

import InviteLearnersModal from 'bundles/enterprise-admin-learners/components/invite-learners/InviteLearnersModal';
import type {
  InviteLearnersModalApi,
  OpenInviteLearnersModalInput,
} from 'bundles/enterprise-admin-learners/components/invite-learners/InviteLearnersModalContext';
import { InviteLearnersModalContext } from 'bundles/enterprise-admin-learners/components/invite-learners/InviteLearnersModalContext';
import type {
  InviteCompletionDetails,
  InviteEntryPoint,
} from 'bundles/enterprise-admin-learners/components/invite-learners/types';

type Props = {
  children: React.ReactNode;
};

const NOOP = () => {};

/**
 * Owns the "is the invite-learners modal open, for which entry point" state
 * and renders the modal in-place (CDS Dialog handles the portal). Mount once
 * near the admin-dashboard root so any descendant can call
 * `useInviteLearnersModal().openModal({ entryPoint, ... })` from anywhere —
 * mirrors `AIMessageModalProvider`.
 */
const InviteLearnersModalProvider = ({ children }: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [entryPoint, setEntryPoint] = React.useState<InviteEntryPoint>('home');
  const [defaultGroupId, setDefaultGroupId] = React.useState<string | undefined>(undefined);
  const [lockGroupSelection, setLockGroupSelection] = React.useState(false);
  // Stored as `() => callback` so React doesn't treat the callback as a state updater.
  const [onInvited, setOnInvited] = React.useState<(details?: InviteCompletionDetails) => void>(() => NOOP);

  const api = React.useMemo<InviteLearnersModalApi>(
    () => ({
      openModal: (input: OpenInviteLearnersModalInput) => {
        setEntryPoint(input.entryPoint);
        setDefaultGroupId(input.defaultGroupId);
        setLockGroupSelection(input.lockGroupSelection ?? false);
        setOnInvited(() => input.onInvited ?? NOOP);
        setIsOpen(true);
      },
      closeModal: () => setIsOpen(false),
    }),
    []
  );

  return (
    <InviteLearnersModalContext.Provider value={api}>
      {children}
      <InviteLearnersModal
        open={isOpen}
        onClose={api.closeModal}
        entryPoint={entryPoint}
        defaultGroupId={defaultGroupId}
        lockGroupSelection={lockGroupSelection}
        onInvited={onInvited}
      />
    </InviteLearnersModalContext.Provider>
  );
};

export default InviteLearnersModalProvider;
